import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ChangeEvent, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SwitchToggle from "../../components/SwitchToggle";

const AddEditSparePart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAddUser = location.pathname === "/manage-spare-parts/add";

  const [showroomImage, setShowroomImage] = useState<string>("");

  const dealers = ["Tesla", "Honda", "Toyota", "Ford"];
  const categories = [
    "Spare Parts",
  ];

  const subCategories = [
    "Engine",
    "Suspension",
    "Transmission",
    "Electrical",
    "Body Parts",
    "Brakes",
    "Tires & Wheels",
    "Oils & Fluids",
    "Accessories",
  ];

  const conditions = ["New", "Used", "Refurbished"];
  const brands = ["Tesla", "Honda", "Toyota", "Ford"];
  const models = ["Model X", "Model Y", "Civic", "Corolla"];
  const years = ["2024", "2023", "2022", "2021"];  

  return (
    <>
      {/* PAGE TITLE */}
      <div className="main_title">
        <h1>{isAddUser ? "Add" : "Edit"} Spare Part</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-spare-parts")}>
            Listed Spare Parts
          </button>{" "}
          - {isAddUser ? "Add" : "Edit"} Spare Part
        </p>
      </div>

      <div className="form">

        {/* ================= Spare Part Detail ================= */}
        <div className="cards">
          <div className="table_header mb_20">
            <h2>Spare Part Detail</h2>
          </div>

          <div className="gap_p">

            {/* Image Upload */}
            <div className="control_group w_100">
              <label>Upload Images</label>
              <div className="gap_p">
                {[1, 2, 3].map((_, i) => (
                  <div className="w_25" key={i}>
                    {showroomImage ? (
                      <div className="upload_image no_radius v2">
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia component="img" image={showroomImage} />
                          </figure>
                          <CloseIcon
                            className="remove_img"
                            onClick={() => setShowroomImage("")}
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="upload_image no_radius v2">
                        <Input
                          sx={{ display: "none" }}
                          type="file"
                          inputProps={{ accept: "image/*" }}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const files = event.target.files;
                            if (files && files[0]) {
                              setShowroomImage(URL.createObjectURL(files[0]));
                            }
                          }}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt=""
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt=""
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Spare Part Name */}
            <div className="control_group w_100">
              <label>Spare Part Name</label>
              <TextField placeholder="Enter Spare Part Name" hiddenLabel fullWidth />
            </div>

            {/* Dealer */}
            <div className="control_group w_33">
              <label>Dealer</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Dealer</MenuItem>
                {dealers.map((dealer) => (
                  <MenuItem key={dealer} value={dealer}>
                    {dealer}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Category */}
            <div className="control_group w_33">
              <label>Category</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* SubCategory */}
            <div className="control_group w_33">
              <label>Sub Category</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Sub Category</MenuItem>
                {subCategories.map((sub) => (
                  <MenuItem key={sub} value={sub}>
                    {sub}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* OEM */}
            <div className="control_group w_33">
              <label>OEM Number</label>
              <TextField placeholder="Enter OEM Number" hiddenLabel fullWidth />
            </div>

            {/* Condition */}
            <div className="control_group w_33">
              <label>Condition</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Condition</MenuItem>
                {conditions.map((cond) => (
                  <MenuItem key={cond} value={cond}>
                    {cond}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Prices */}
            <div className="control_group w_33">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label>Actual Price</label>
                  <TextField
                    placeholder="Actual Price"
                    hiddenLabel
                    fullWidth
                  />
                </div>

                <div className="control_group w_50">
                  <label>Discounted Price</label>
                  <TextField
                    placeholder="Discounted Price"
                    hiddenLabel
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* Small Description */}
            <div className="control_group w_100">
              <label>Small Description</label>
              <TextField
                multiline
                rows={2}
                placeholder="Enter short description"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* Full Description */}
            <div className="control_group w_100">
              <label>Full Description</label>
              <TextField
                multiline
                rows={5}
                placeholder="Enter full description"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* Featured */}
            <div className="control_group flex w_100">
              <label>Is this Featured Spare Part?</label>
              <SwitchToggle />
            </div>
          </div>
        </div>

        {/* ================= Vehicle Compatibility Detail ================= */}
        <div className="cards">
          <div className="table_header mb_20">
            <h2>Vehicle Compatibility Detail</h2>
          </div>

          <div className="gap_p">
            <div className="control_group w_33">
              <label>Make</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Make</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Model</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Model</MenuItem>
                {models.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Year</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Year</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary">Save</Button>
        </div>
      </div>
    </>
  );
};

export default AddEditSparePart;